import React from "react";

import { getDependents, useGetApplicationQuery, useGetPlanCostsQuery, useGetPlansQuery } from "EmployeeApplication/api";
import { TermLifeContextProvider } from "EmployeeCoverage/Components/TermLife/termLife.context";
import { TermLifeEnrollmentModals } from "EmployeeCoverage/Components/TermLife/termLife.modals";
import { TermLifeMultiplePlans } from "EmployeeCoverage/Components/TermLife/termLife.multiplePlans";
import { useSelector } from "react-redux";
import { TermLifePlan, PlanCost } from "Utils/types";
import { BasicLoading } from "ZeroRuby";

import { TermLifeSinglePlan } from "./termLife.singlePlan";

type Data =
  | {
      isLoading: true;
      plans: any;
      planCosts: any;
    }
  | {
      isLoading: false;
      plans: Array<TermLifePlan>;
      planCosts: Array<PlanCost>;
    };

const useData = (): Data => {
  const { isLoading: isLoadingApplication, data: { employeePolicy: { id } = {} } = {} } = useGetApplicationQuery();
  const { isLoading: isLoadingPlans, data: { plans = [] } = {} } = useGetPlansQuery(
    {
      id,
      type: "term_life",
    },
    { skip: !id },
  );
  const dependents = useSelector(getDependents) || [];
  const dependentIDs = dependents.map((dependent) => dependent.id);
  const { isLoading: isLoadingCosts, data: { planCosts } = {} } = useGetPlanCostsQuery(
    {
      id,
      type: "term_life",
      dependentIDs,
    },
    { skip: plans.length === 0 },
  );

  return {
    isLoading: isLoadingApplication || isLoadingPlans || isLoadingCosts,
    plans,
    planCosts,
  } as Data;
};

/**
 * This component manages what to render as term life plans and costs are loaded.
 */
const StateManager = () => {
  const { isLoading, plans = [], planCosts = [] } = useData();
  if (isLoading) {
    return <BasicLoading />;
  }
  if (plans.length === 1) {
    return <TermLifeSinglePlan plan={plans[0]} planCost={planCosts.find(({ planID }) => planID === plans[0].id)} />;
  }
  if (plans.length > 1) {
    return <TermLifeMultiplePlans plans={plans} planCosts={planCosts} />;
  }
  return null;
};

export const TermLifePage = () => (
  <TermLifeContextProvider>
    <StateManager />
    <TermLifeEnrollmentModals />
  </TermLifeContextProvider>
);
